"use client";

import React, { useEffect, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import WorkerDetails from "./WorkerDetails";
import WorkerOpeningHours from "./WorkerOpeningHours";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectUser } from "../../redux/slices/profileSlice/ProfileSlice";

const API_URL = REST_API_URL;

export default function YourBusinessWorkers() {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const user = useAppSelector(selectUser);
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);

        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/auth/workers-list/`, {
          headers,
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || `HTTP ${res.status}`);
        }

        const data = await res.json();
        setWorkers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Failed to fetch workers:", err);
        toast.error(t("home.failedLoadWorkers"));
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchWorkers();
  }, [token, t]);

  const filteredWorkers = workers.filter(
    (w) => w.worker_email !== user?.user?.email,
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 w-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[20px] font-bold text-[#302B2B]">
          {t("home.workersList")}
        </h3>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="w-6 h-6 text-[#020637] animate-spin" />
        </div>
      )}

      {!loading && workers.length === 0 && (
        <p className="text-center text-[#9A99AB] py-6">
          {t("home.noWorkersFound")}
        </p>
      )}

      {!loading && workers.length > 0 && (
        <div className="flex flex-col gap-4 max-h-[650px] overflow-y-auto pr-2">
          {filteredWorkers.map((w, i) => {
            const isUnregistered = !w.full_name || w.full_name.trim() === "";
            return (
              <div
                key={i}
                // onClick={() => !isUnregistered && setSelectedWorker(w)}
                className={`flex flex-col border border-[#EDEEF3] rounded-2xl px-4 py-3 transition
                  ${isUnregistered ? "bg-gray-50 cursor-not-allowed opacity-60" : ""}
                `}
              >
                <div className="flex items-center gap-3 mb-3">
                  {!isUnregistered && (
                    <img
                      src={w.image_url || ""}
                      alt={w.full_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="flex flex-col">
                    <p className="font-semibold text-sm text-[#302B2B]">
                      {isUnregistered
                        ? t("home.unregisteredUser")
                        : w.full_name}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-[#9A99AB]">
                      <Mail className="w-3 h-3" />
                      <span>{w.worker_email || t("home.noEmail")}</span>
                    </div>
                  </div>
                </div>

                {!isUnregistered && (
                  <WorkerOpeningHours workDays={w.work_days} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
